import { CSSProperties, useState } from 'react'
import classes from './ChatWidget.module.scss'
import { ButtonDefinition } from './types'
import {
  IconCalendar,
  IconChevron,
  IconGrid,
  IconSpark,
  IconSupport,
  IconTag,
} from './icons'

interface Props {
  buttons: ButtonDefinition[]
  onButton: (b: ButtonDefinition) => void
  disabled?: boolean
  /** İlk yükleme stagger'ı için (örn. animationDelay). */
  style?: CSSProperties
}

export interface Group {
  key: string
  label: string
  items: ButtonDefinition[]
}

/** Düz buton listesini `Group` alanına göre, ilk-görülme sırasını koruyarak gruplar. */
export function groupButtons(buttons: ButtonDefinition[]): Group[] {
  const order: string[] = []
  const map = new Map<string, Group>()
  buttons.forEach((b, i) => {
    const label = (b.Group || '').trim()
    // Grupsuz butonlar kendi başına tekil kart olur (benzersiz anahtar).
    const key = label || `__solo_${b.Intent || i}`
    if (!map.has(key)) {
      order.push(key)
      map.set(key, { key, label, items: [] })
    }
    map.get(key)!.items.push(b)
  })
  return order.map(k => map.get(k)!)
}

/** Grup adına göre ikon seçer (Türkçe anahtar kelime eşleştirmesi). */
export function getGroupIcon(label: string) {
  const s = label.toLocaleLowerCase('tr')
  if (/(fiyat|ücret|price|stok|bayi)/.test(s)) return IconTag
  if (/(destek|support|teknik|yardım|sorun|talep)/.test(s)) return IconSupport
  if (/(ürün|product|özellik|katalog|spec)/.test(s)) return IconGrid
  if (/(demo|randevu|takvim|toplant|görüşme)/.test(s)) return IconCalendar
  return IconSpark
}

export default function ActionCards({ buttons, onButton, disabled, style }: Props) {
  const groups = groupButtons(buttons)
  const [openKey, setOpenKey] = useState<string | null>(null)

  if (groups.length === 0) return null

  return (
    <div className={classes.actionCards} style={style}>
      {groups.map(g => {
        // Grupsuz tekil buton → doğrudan eylem kartı (açılmaz).
        if (!g.label) {
          const b = g.items[0]
          const Icon = getGroupIcon(b.Label)
          return (
            <button
              key={g.key}
              type='button'
              className={classes.actionSolo}
              onClick={() => onButton(b)}
              disabled={disabled}
            >
              <span className={classes.actionIcon}>
                <Icon />
              </span>
              <span className={classes.actionLabel}>{b.Label}</span>
              <span className={classes.actionChevronSolo}>
                <IconChevron />
              </span>
            </button>
          )
        }

        const Icon = getGroupIcon(g.label)
        const open = openKey === g.key
        return (
          <div key={g.key} className={`${classes.actionCard} ${open ? classes.actionCardOpen : ''}`}>
            <button
              type='button'
              className={classes.actionCardHead}
              onClick={() => setOpenKey(open ? null : g.key)}
              aria-expanded={open}
            >
              <span className={classes.actionIcon}>
                <Icon />
              </span>
              <span className={classes.actionLabel}>{g.label}</span>
              <span className={`${classes.actionChevron} ${open ? classes.actionChevronUp : ''}`}>
                <IconChevron />
              </span>
            </button>
            {open && (
              <div className={classes.actionItems}>
                {g.items.map(b => (
                  <button
                    key={b.Intent}
                    type='button'
                    className={classes.actionItem}
                    onClick={() => onButton(b)}
                    disabled={disabled}
                    title={b.Label}
                  >
                    {b.Label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
